import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, Users, Search, Building2, Shield, Plus, Edit3, Save, X, Trash2, CheckCircle2,
  AlertTriangle, Loader2, ChevronDown, Mail, Clock, History, RefreshCw, Filter,
} from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/format";

const SHEETS = [
  { value: "TTF", label: "Forward Gas TTF", unit: "€/MWh" },
  { value: "PSV", label: "Forward Gas PSV", unit: "€/MWh" },
  { value: "Power", label: "Forward Power", unit: "€/MWh" },
  { value: "CO2", label: "Forward CO2", unit: "€/tCO2" },
];

const DEFAULT_FORM = {
  source_sheet: "TTF",
  contract_name: "",
  direction: "above",
  threshold: "",
  enabled: true,
  referenced_client_id: "",  // company_id — OBBLIGATORIO
  notify_scope: "all_users", // "all_users" | "specific" | "none"
  notify_user_ids: [],
  expires_at: "",
};

const fmtDateIt = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : s;
};

/**
 * Backoffice "Gestione Alert Prezzi".
 *
 * Regole:
 *  - Ogni alert è SEMPRE associato a un'AZIENDA (company_id, obbligatoria).
 *  - Email a direzione@adybo.it: AUTOMATICA (sempre inviata, nessun toggle).
 *  - Destinatari push+email agli utenti dell'azienda: 3 opzioni
 *      · "all_users"  → tutti gli utenti dell'azienda
 *      · "specific"   → solo alcuni (multi-select)
 *      · "none"       → nessuno (solo direzione riceve email)
 *  - Nome azienda in prima linea nella lista alert.
 */
export default function AdminPriceAlerts() {
  const [companies, setCompanies] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [companySearch, setCompanySearch] = useState("");
  const [tab, setTab] = useState("admin"); // "admin" | "client"
  const [viewMode, setViewMode] = useState("active"); // "active" | "history"
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histContract, setHistContract] = useState("");
  const [histDateFrom, setHistDateFrom] = useState("");
  const [histDateTo, setHistDateTo] = useState("");
  const [renewingId, setRenewingId] = useState(null);
  const [renewDate, setRenewDate] = useState("");
  const [listCompanyFilter, setListCompanyFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmState, setConfirmState] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [evalBusy, setEvalBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const cs = (data || [])
          .filter((c) => (c.users_count ?? 0) >= 0)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCompanies(cs);
      } catch (e) {
        toast.error("Errore caricamento aziende");
      }
    })();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const params = { scope: tab, status: "active" };
      if (tab === "admin" && listCompanyFilter) params.referenced_client_id = listCompanyFilter;
      const { data } = await api.get("/price-alerts", { params });
      setAlerts(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento alert");
    } finally {
      setLoading(false);
    }
  };
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { scope: tab, status: "history" };
      if (tab === "admin" && listCompanyFilter) params.referenced_client_id = listCompanyFilter;
      if (histContract) params.contract_name = histContract;
      if (histDateFrom) params.date_from = histDateFrom;
      if (histDateTo) params.date_to = histDateTo;
      const { data } = await api.get("/price-alerts", { params });
      setHistory(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento storico");
    } finally {
      setHistoryLoading(false);
    }
  };
  useEffect(() => { loadAlerts(); /* eslint-disable-next-line */ }, [tab, listCompanyFilter]);
  useEffect(() => {
    if (viewMode === "history") loadHistory();
    // eslint-disable-next-line
  }, [viewMode, tab, listCompanyFilter, histContract, histDateFrom, histDateTo]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/price-alerts/contracts", { params: { source_sheet: form.source_sheet } });
        setContracts(data.contracts || []);
        if (form.contract_name && !(data.contracts || []).includes(form.contract_name)) {
          setForm((prev) => ({ ...prev, contract_name: "" }));
        }
      } catch (e) {
        setContracts([]);
      }
    })();
    // eslint-disable-next-line
  }, [form.source_sheet]);

  useEffect(() => {
    if (!form.referenced_client_id) { setCompanyUsers([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/price-alerts/company-users/${form.referenced_client_id}`);
        setCompanyUsers(data);
      } catch (e) {
        setCompanyUsers([]);
      }
    })();
    // eslint-disable-next-line
  }, [form.referenced_client_id]);

  const currentSheet = SHEETS.find((s) => s.value === form.source_sheet) || SHEETS[0];
  const selectedCompany = companies.find((c) => c.id === form.referenced_client_id);

  const filteredCompanies = companies.filter((c) => {
    if (!companySearch) return true;
    return (c.name || "").toLowerCase().includes(companySearch.toLowerCase());
  });

  const resetForm = () => { setForm(DEFAULT_FORM); setEditingId(null); };

  const submit = async () => {
    if (!form.referenced_client_id) return toast.error("Seleziona un'azienda");
    if (!form.contract_name) return toast.error("Seleziona un contratto");
    const th = parseFloat(form.threshold);
    if (isNaN(th) || th <= 0) return toast.error("Inserisci una soglia numerica > 0");
    if (form.notify_scope === "specific" && form.notify_user_ids.length === 0) {
      return toast.error("Seleziona almeno un utente destinatario");
    }
    setBusy(true);
    try {
      const payload = {
        source_sheet: form.source_sheet,
        contract_name: form.contract_name,
        direction: form.direction,
        threshold: th,
        enabled: form.enabled,
        admin_managed: true,
        referenced_client_id: form.referenced_client_id,
        notify_scope: form.notify_scope,
        notify_user_ids: form.notify_scope === "specific" ? form.notify_user_ids : [],
        expires_at: form.expires_at || null,
      };
      if (editingId) {
        await api.patch(`/price-alerts/${editingId}`, payload);
        toast.success("Alert aggiornato");
      } else {
        await api.post("/price-alerts", payload);
        toast.success("Alert creato");
      }
      resetForm();
      await loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (a) => {
    setForm({
      source_sheet: a.source_sheet,
      contract_name: a.contract_name,
      direction: a.direction,
      threshold: String(a.threshold),
      enabled: !!a.enabled,
      referenced_client_id: a.referenced_client_id || "",
      notify_scope: a.notify_scope || "all_users",
      notify_user_ids: a.notify_user_ids || [],
      expires_at: a.expires_at ? String(a.expires_at).slice(0, 10) : "",
    });
    setEditingId(a.id);
    setViewMode("active");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doRenew = async (a) => {
    try {
      const payload = renewDate ? { expires_at: renewDate } : {};
      await api.post(`/price-alerts/${a.id}/renew`, payload);
      toast.success("Alert rinnovato");
      setRenewingId(null);
      setRenewDate("");
      await Promise.all([loadAlerts(), loadHistory()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rinnovo");
    }
  };

  const removeAlert = (a) => {
    setConfirmState({
      title: "Eliminare l'alert?",
      message: `${a.source_sheet} ${a.contract_name} — soglia ${a.threshold}.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/price-alerts/${a.id}`);
          toast.success("Alert eliminato");
          await loadAlerts();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const closeAlert = (a) => {
    setConfirmState({
      title: "Chiudere l'alert?",
      message: `${a.source_sheet} ${a.contract_name} — soglia ${a.threshold}. Viene spostato nello Storico; il cliente potrà rinnovarlo.`,
      confirmLabel: "Chiudi alert",
      variant: "default",
      onConfirm: async () => {
        try {
          await api.post(`/price-alerts/${a.id}/close`);
          toast.success("Alert chiuso");
          await Promise.all([loadAlerts(), loadHistory()]);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore chiusura");
        }
      },
    });
  };

  const toggleDirezione = async (a) => {
    const next = !a.notify_direzione;
    try {
      await api.patch(`/price-alerts/${a.id}`, { notify_direzione: next });
      toast.success(next ? "Email direzione riattivata" : "Email direzione disattivata");
      await loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/price-alerts/${a.id}`, { enabled: !a.enabled });
      await loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleUserInList = (uid) => {
    setForm((prev) => {
      const has = prev.notify_user_ids.includes(uid);
      return {
        ...prev,
        notify_user_ids: has ? prev.notify_user_ids.filter((x) => x !== uid) : [...prev.notify_user_ids, uid],
      };
    });
  };

  const runEvaluate = async () => {
    setEvalBusy(true);
    try {
      const { data } = await api.post("/price-alerts/evaluate");
      toast.success(`Valutati ${data.evaluated} · scattati ${data.fired} · scaduti ${data.expired || 0}`);
      await loadAlerts();
      if (viewMode === "history") await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setEvalBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-price-alerts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Bell className="text-blue-600" size={26} /> Gestione Alert Prezzi
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
            <Mail size={12} /> Email automatica a <b>direzione@adybo.it</b> · Push+email agli utenti dell'azienda selezionata (tutti / alcuni / nessuno).
          </p>
        </div>
        <button
          onClick={runEvaluate}
          disabled={evalBusy}
          className="btn-brand text-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="admin-alerts-eval"
        >
          {evalBusy ? "…" : "Rivaluta ora"}
        </button>
      </div>

      <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner">
        <button
          onClick={() => { setTab("admin"); setListCompanyFilter(""); }}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-2 ${tab === "admin" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
          data-testid="admin-alerts-tab-admin"
        >
          <Shield size={14} /> Alert Staff
        </button>
        <button
          onClick={() => { setTab("client"); setListCompanyFilter(""); }}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold flex items-center gap-2 ${tab === "client" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
          data-testid="admin-alerts-tab-client"
        >
          <Users size={14} /> Alert dei clienti
        </button>
      </div>

      {tab === "admin" && (
        <div className="card-elevated p-5" data-testid="admin-alerts-form">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="text-blue-600" size={18} />
            <h2 className="font-display font-bold text-slate-900">
              {editingId ? "Modifica alert" : "Nuovo alert soglia"}
            </h2>
            {editingId && (
              <button onClick={resetForm} className="ml-auto text-xs text-slate-500 hover:text-red-600 flex items-center gap-1">
                <X size={13} /> Annulla modifica
              </button>
            )}
          </div>

          {/* STEP 1 — Azienda (obbligatoria, in prima impressione) */}
          <div className="mb-5">
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-2">
              <Building2 size={12} /> Azienda cliente <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <div
                onClick={() => setShowCompanyDropdown((v) => !v)}
                className={`w-full px-3 py-2.5 rounded-lg border cursor-pointer flex items-center gap-2 text-sm bg-white ${selectedCompany ? "border-blue-500" : "border-slate-200 hover:border-blue-400"}`}
                data-testid="alert-company-picker"
              >
                {selectedCompany ? (
                  <>
                    <Building2 size={14} className="text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{selectedCompany.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {selectedCompany.users_count || 0} utent{selectedCompany.users_count === 1 ? "e" : "i"}
                        {selectedCompany.is_demo_company ? " · demo" : ""}
                      </div>
                    </div>
                  </>
                ) : (
                  <span className="text-slate-400">— seleziona azienda —</span>
                )}
                <ChevronDown size={14} className={`text-slate-400 flex-shrink-0 transition-transform ${showCompanyDropdown ? "rotate-180" : ""}`} />
              </div>
              {showCompanyDropdown && (
                <div className="absolute z-30 mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-xl max-h-80 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-slate-100">
                    <div className="relative">
                      <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        autoFocus
                        type="text"
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        placeholder="Cerca azienda…"
                        className="w-full pl-7 pr-2 py-1.5 text-sm rounded border border-slate-200 outline-none focus:border-blue-500"
                        data-testid="alert-company-search"
                      />
                    </div>
                  </div>
                  <ul className="overflow-y-auto divide-y divide-slate-100 flex-1">
                    {filteredCompanies.length === 0 ? (
                      <li className="p-4 text-xs text-slate-400 text-center">Nessuna azienda</li>
                    ) : filteredCompanies.map((c) => (
                      <li
                        key={c.id}
                        onClick={() => { setForm({ ...form, referenced_client_id: c.id, notify_user_ids: [] }); setShowCompanyDropdown(false); setCompanySearch(""); }}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${form.referenced_client_id === c.id ? "bg-blue-50" : ""}`}
                        data-testid={`alert-company-option-${c.id}`}
                      >
                        <div className="font-semibold text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500">
                          {c.users_count || 0} utent{c.users_count === 1 ? "e" : "i"}
                          {c.is_demo_company ? " · demo" : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 — Curva + Contratto + Direzione + Soglia */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Curva forward</label>
              <select
                value={form.source_sheet}
                onChange={(e) => setForm({ ...form, source_sheet: e.target.value, contract_name: "" })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="alert-sheet-select"
              >
                {SHEETS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Contratto</label>
              <select
                value={form.contract_name}
                onChange={(e) => setForm({ ...form, contract_name: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="alert-contract-select"
              >
                <option value="">— seleziona —</option>
                {contracts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="text-[10px] text-slate-400 mt-1">{contracts.length} contratti (dal più recente)</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Direzione</label>
              <div className="mt-1 inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner w-full">
                <button
                  onClick={() => setForm({ ...form, direction: "above" })}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${form.direction === "above" ? "bg-white shadow text-red-600" : "text-slate-500"}`}
                  data-testid="alert-direction-above"
                >≥ Soglia (sale)</button>
                <button
                  onClick={() => setForm({ ...form, direction: "below" })}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${form.direction === "below" ? "bg-white shadow text-emerald-600" : "text-slate-500"}`}
                  data-testid="alert-direction-below"
                >≤ Soglia (scende)</button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Soglia ({currentSheet.unit})</label>
              <input
                type="number"
                step="0.001"
                value={form.threshold}
                onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                placeholder={`Es. ${form.source_sheet === "CO2" ? "80" : "45"}`}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                data-testid="alert-threshold-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1">
                <Clock size={11} /> Scadenza (opzionale)
              </label>
              <input
                type="date"
                value={form.expires_at || ""}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                min={new Date().toISOString().slice(0, 10)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                data-testid="alert-expires-input"
              />
              <div className="text-[10px] text-slate-400 mt-1">Dopo questa data l&apos;alert viene chiuso automaticamente.</div>
            </div>
          </div>

          {/* STEP 3 — Destinatari utenti dell'azienda (push+email uniti) */}
          <div className="mt-5 border-t border-slate-100 pt-4">
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-2 mb-2">
              <Users size={12} /> Notifica push + email agli utenti dell'azienda
            </label>
            {!selectedCompany ? (
              <div className="text-xs text-slate-400 italic">Seleziona prima un'azienda.</div>
            ) : (
              <>
                <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner">
                  <button
                    onClick={() => setForm({ ...form, notify_scope: "all_users", notify_user_ids: [] })}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${form.notify_scope === "all_users" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
                    data-testid="alert-scope-all"
                  >Tutti gli utenti</button>
                  <button
                    onClick={() => setForm({ ...form, notify_scope: "specific" })}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${form.notify_scope === "specific" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
                    data-testid="alert-scope-specific"
                  >Solo alcuni</button>
                  <button
                    onClick={() => setForm({ ...form, notify_scope: "none", notify_user_ids: [] })}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold ${form.notify_scope === "none" ? "bg-white shadow text-slate-700" : "text-slate-500"}`}
                    data-testid="alert-scope-none"
                  >Nessuno</button>
                </div>
                {form.notify_scope === "specific" && (
                  <div className="mt-3 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50" data-testid="alert-users-multiselect">
                    {companyUsers.length === 0 ? (
                      <div className="text-xs text-slate-400 text-center py-4">Nessun utente per questa azienda.</div>
                    ) : companyUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-white rounded px-2">
                        <input
                          type="checkbox"
                          checked={form.notify_user_ids.includes(u.id)}
                          onChange={() => toggleUserInList(u.id)}
                          data-testid={`alert-user-cb-${u.id}`}
                        />
                        <span className="text-slate-800">{u.full_name || u.email}</span>
                        {u.full_name && <span className="text-xs text-slate-400">— {u.email}</span>}
                      </label>
                    ))}
                    {form.notify_user_ids.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500">
                        {form.notify_user_ids.length} utente/i selezionato/i
                      </div>
                    )}
                  </div>
                )}
                {form.notify_scope === "all_users" && (
                  <div className="mt-2 text-xs text-slate-500">
                    Push+email a <b>tutti gli utenti</b> di <b>{selectedCompany.name}</b>.
                  </div>
                )}
                {form.notify_scope === "none" && (
                  <div className="mt-2 text-xs text-slate-500">
                    Solo email automatica a <b>direzione@adybo.it</b> — nessun utente riceverà notifica.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Note email automatica + submit */}
          <div className="mt-5 border-t border-slate-100 pt-4 flex items-center gap-4 flex-wrap">
            <div className="text-xs text-slate-600 flex items-center gap-1.5">
              <Mail size={13} className="text-purple-600" />
              Email a <b>direzione@adybo.it</b> inviata sempre automaticamente
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="alert-enabled-toggle" />
              Attivo
            </label>
            <button
              onClick={submit}
              disabled={busy}
              className="btn-brand ml-auto flex items-center gap-2 text-sm disabled:opacity-60"
              data-testid="alert-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : (editingId ? <Save size={14} /> : <Plus size={14} />)}
              {editingId ? "Salva modifiche" : "Crea alert"}
            </button>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            L&apos;alert scatta al <b>primo cross</b> della soglia (Ask, Last o Previous Close se non quotato).
            Al primo trigger viene chiuso e spostato nello <b>Storico</b>: da lì si può rinnovare con un clic (opzionalmente con nuova scadenza).
          </div>
        </div>
      )}

      {/* Filtro azienda per la lista (solo tab admin) */}
      {tab === "admin" && (
        <div className="card-elevated p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Filtra lista per azienda</span>
            <select
              value={listCompanyFilter}
              onChange={(e) => setListCompanyFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500 min-w-[280px]"
              data-testid="admin-alerts-list-company-filter"
            >
              <option value="">— tutte le aziende —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="text-xs text-slate-400">{alerts.length} alert</div>
          </div>
        </div>
      )}

      {/* Lista alert con tab Attivi/Storico */}
      <div className="card-elevated overflow-hidden">
        <div className="px-4 pt-3 border-b border-slate-100 flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setViewMode("active")}
            className={`px-3 py-2 text-sm font-semibold rounded-t-md flex items-center gap-2 ${viewMode === "active" ? "text-blue-600 border-b-2 border-blue-600 -mb-[1px]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="admin-alerts-viewmode-active"
          >
            <Bell size={14} /> Attivi <span className="text-xs font-normal">({alerts.length})</span>
          </button>
          <button
            onClick={() => setViewMode("history")}
            className={`px-3 py-2 text-sm font-semibold rounded-t-md flex items-center gap-2 ${viewMode === "history" ? "text-blue-600 border-b-2 border-blue-600 -mb-[1px]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="admin-alerts-viewmode-history"
          >
            <History size={14} /> Storico
          </button>
          <div className="ml-auto text-xs text-slate-400 pr-2">
            {viewMode === "active" ? `${alerts.length} alert` : `${history.length} chiusi`}
          </div>
        </div>

        {viewMode === "history" && (
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap bg-slate-50/50">
            <Filter size={14} className="text-slate-400" />
            <select
              value={histContract}
              onChange={(e) => setHistContract(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="admin-hist-contract"
            >
              <option value="">Contratto: tutti</option>
              {Array.from(new Set(history.map((h) => h.contract_name))).sort().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="date"
              value={histDateFrom}
              onChange={(e) => setHistDateFrom(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="admin-hist-date-from"
            />
            <input
              type="date"
              value={histDateTo}
              onChange={(e) => setHistDateTo(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-500"
              data-testid="admin-hist-date-to"
            />
            {(histContract || histDateFrom || histDateTo) && (
              <button
                onClick={() => { setHistContract(""); setHistDateFrom(""); setHistDateTo(""); }}
                className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 ml-1"
              >
                <X size={12} /> Pulisci
              </button>
            )}
            <span className="ml-auto text-xs text-slate-400">filtro cliente via dropdown sopra</span>
          </div>
        )}

        {(() => {
          const list = viewMode === "active" ? alerts : history;
          const isLoading = viewMode === "active" ? loading : historyLoading;
          const isHistory = viewMode === "history";
          if (isLoading) return <div className="p-10 text-center text-sm text-slate-400">Caricamento…</div>;
          if (list.length === 0) return <div className="p-10 text-center text-sm text-slate-500">Nessun alert.</div>;
          return (
            <ul className="divide-y divide-slate-100">
              {list.map((a) => {
                const unit = SHEETS.find((s) => s.value === a.source_sheet)?.unit || "€/MWh";
                const dirColor = a.direction === "above" ? "text-red-600" : "text-emerald-600";
                const dirSign = a.direction === "above" ? "≥" : "≤";
                const isTriggered = a.state === "triggered";
                const companyName = a.admin_managed
                  ? (a.referenced_client_company_name || "—")
                  : (a.user_company_name || a.user_full_name || a.user_email || "—");
                const recipientsLine = a.admin_managed
                  ? (a.notify_scope === "specific"
                    ? `${a.notify_user_labels?.length || 0} utenti: ${(a.notify_user_labels || []).map((x) => x.label).join(", ")}`
                    : a.notify_scope === "none"
                    ? "Nessun utente notificato (solo direzione@adybo.it)"
                    : "Tutti gli utenti dell'azienda")
                  : null;
                const reason = a.closed_reason || "";
                const reasonBadge = reason === "fired" ? { text: "Scattato", cls: "bg-red-100 text-red-700" }
                  : reason === "expired" ? { text: "Scaduto", cls: "bg-amber-100 text-amber-700" }
                  : reason === "manually_disabled" || reason === "admin_disabled" ? { text: "Chiuso", cls: "bg-slate-200 text-slate-700" }
                  : null;
                return (
                  <li
                    key={a.id}
                    className={`px-5 py-4 flex items-start gap-3 ${!isHistory && !a.enabled ? "opacity-60" : ""}`}
                    data-testid={`alert-row-${a.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isTriggered ? "bg-red-100 text-red-600" : isHistory ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                      {isTriggered ? <AlertTriangle size={18} /> : <Bell size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 size={14} className="text-blue-600 flex-shrink-0" />
                        <span className="text-base font-display font-bold text-slate-900" data-testid={`alert-client-${a.id}`}>
                          {companyName}
                        </span>
                        {a.admin_managed && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider">Staff</span>
                        )}
                        {reasonBadge && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${reasonBadge.cls}`}>{reasonBadge.text}</span>
                        )}
                        {!isHistory && isTriggered && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold uppercase tracking-wider flex items-center gap-1">
                            <CheckCircle2 size={10} /> Scattato
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-700 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="font-semibold">{a.source_sheet} · {a.contract_name}</span>
                        <span className={`font-semibold ${dirColor}`}>{dirSign} {a.threshold} {unit}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span data-testid={`alert-created-${a.id}`}>
                          creato: {fmtDateIt(a.created_at)}
                          {a.created_by_full_name && (
                            <span> · da <b className="text-slate-700">{a.created_by_full_name}</b>
                              {a.created_by_role && a.created_by_role !== "cliente" && (
                                <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700 uppercase tracking-wider">{a.created_by_role}</span>
                              )}
                            </span>
                          )}
                        </span>
                        {a.expires_at && !isHistory && (
                          <span className="text-amber-600 flex items-center gap-1">
                            <Clock size={11} /> scade il {fmtDateIt(a.expires_at)}
                          </span>
                        )}
                        {a.closed_at && (
                          <span className="text-slate-500">
                            chiuso: {fmtDateTime(a.closed_at)}
                            {reason === "fired" && a.last_triggered_value != null && <span> @ {a.last_triggered_value}</span>}
                          </span>
                        )}
                      </div>
                      {recipientsLine && (
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                          <Users size={11} /> {recipientsLine}
                        </div>
                      )}
                      {a.admin_managed && a.email_recipient && (
                        <div className="text-xs text-purple-600 mt-0.5 flex items-center gap-1.5">
                          <Mail size={11} /> Email automatica → {a.email_recipient}
                        </div>
                      )}
                      {!isHistory && isTriggered && (
                        <div className="text-xs text-red-600 mt-0.5">
                          Ultimo trigger: {fmtDateTime(a.last_triggered_at)} @ {a.last_triggered_value}
                        </div>
                      )}
                      {!isHistory && !a.enabled && <div className="text-xs text-slate-400 mt-0.5">Disattivato</div>}
                      {isHistory && renewingId === a.id && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap bg-blue-50 border border-blue-100 rounded-lg p-2" data-testid={`alert-renew-panel-${a.id}`}>
                          <span className="text-xs text-slate-600">Nuova scadenza (opzionale):</span>
                          <input
                            type="date"
                            value={renewDate}
                            onChange={(e) => setRenewDate(e.target.value)}
                            min={new Date().toISOString().slice(0, 10)}
                            className="px-2 py-1 rounded border border-slate-200 text-xs outline-none focus:border-blue-500"
                            data-testid={`alert-renew-date-${a.id}`}
                          />
                          <button
                            onClick={() => doRenew(a)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                            data-testid={`alert-renew-confirm-${a.id}`}
                          >Conferma rinnovo</button>
                          <button
                            onClick={() => { setRenewingId(null); setRenewDate(""); }}
                            className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:text-red-600"
                          >Annulla</button>
                        </div>
                      )}
                    </div>
                    {!isHistory && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => toggleDirezione(a)}
                          className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1 ${a.notify_direzione === false ? "border-slate-200 text-slate-400 bg-slate-50 hover:border-emerald-300 hover:text-emerald-600" : "border-purple-200 text-purple-700 bg-purple-50 hover:border-purple-400"}`}
                          data-testid={`alert-toggle-direzione-${a.id}`}
                          title={a.notify_direzione === false ? "Riattiva email a direzione@adybo.it" : "Disattiva email a direzione@adybo.it"}
                        >
                          <Mail size={11} /> {a.notify_direzione === false ? "Direz. OFF" : "Direz. ON"}
                        </button>
                        {tab === "admin" && (
                          <button
                            onClick={() => toggleEnabled(a)}
                            className={`text-xs px-2 py-1 rounded border ${a.enabled ? "border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}
                            data-testid={`alert-toggle-${a.id}`}
                          >{a.enabled ? "Sospendi" : "Riprendi"}</button>
                        )}
                        <button
                          onClick={() => closeAlert(a)}
                          className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-600"
                          data-testid={`alert-close-${a.id}`}
                          title="Chiudi alert e sposta nello Storico"
                        >Chiudi</button>
                        {tab === "admin" && (
                          <button
                            onClick={() => startEdit(a)}
                            className="p-1.5 text-slate-500 hover:text-blue-600"
                            title="Modifica"
                            data-testid={`alert-edit-${a.id}`}
                          ><Edit3 size={14} /></button>
                        )}
                        <button
                          onClick={() => removeAlert(a)}
                          className="p-1.5 text-red-500 hover:text-red-700"
                          title="Elimina definitivamente"
                          data-testid={`alert-delete-${a.id}`}
                        ><Trash2 size={14} /></button>
                      </div>
                    )}
                    {isHistory && renewingId !== a.id && (
                      <button
                        onClick={() => { setRenewingId(a.id); setRenewDate(""); }}
                        className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center gap-1"
                        data-testid={`alert-renew-${a.id}`}
                      >
                        <RefreshCw size={12} /> Rinnova
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </div>
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
